<script setup>
import { defineProps, computed } from 'vue'
import { NCard, NAvatar, NSpace, NH2, NText, NDivider } from 'naive-ui'
import { Pie } from 'vue-chartjs'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { useWindowSize } from '@vueuse/core'

ChartJS.register(ArcElement, Tooltip, Legend)

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  noheader: {
    type: Boolean,
    default: false
  },
  onClickUser: {
    type: Function,
    default: () => {}
  }
})

const { width } = useWindowSize()

const avatarSize = computed(() => {
  return width.value < 768 ? 150 : 200
})

function generateColors(num) {
  const colors = []
  for (let i = 0; i < num; i++) {
    const color = `#${Math.floor(Math.random() * 16777215).toString(16)}`
    colors.push(color)
  }
  return colors
}

const brandData = computed(() => {
  const brands =
    props.item.products?.map((product) => product.product.brand) ?? []
  const data = {}
  brands.forEach((brand) => {
    if (!data[brand]) {
      data[brand] = 0
    }
    data[brand] += 1
  })
  // chartjsの渡すデータを生成
  const chartData = {
    labels: Object.keys(data),
    datasets: [
      {
        backgroundColor: generateColors(Object.keys(data).length),
        data: Object.values(data)
      }
    ]
  }

  return chartData
})

const categoryData = computed(() => {
  const values =
    props.item.products?.map((product) => product.product.sub_category) ?? []
  const data = {}
  values.forEach((value) => {
    if (!data[value]) {
      data[value] = 0
    }
    data[value] += 1
  })
  // chartjsの渡すデータを生成
  const chartData = {
    labels: Object.keys(data),
    datasets: [
      {
        backgroundColor: generateColors(Object.keys(data).length),
        data: Object.values(data)
      }
    ]
  }

  return chartData
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'right'
    },
    tooltip: {
      enabled: true
    }
  }
}

const brandCount = computed(() => {
  const brands = props.item.products?.map((product) => product.product.brand)
  return new Set(brands).size
})
const productCount = computed(() => {
  return props.item.products?.length ?? 0
})

const handleTalentClick = (item) => {
  props.onClickUser(item)
}
</script>

<template>
  <NCard id="user-data" class="user-data-card">
    <NTabs type="line" animated>
      <NTabPane name="profile" tab="プロフィール">
        <n-card class="profile-card" :bordered="false">
          <div class="profile-content" v-show="!noheader">
            <n-avatar :src="item.image_url" round :size="avatarSize" />
            <n-h2>{{ item.name }}</n-h2>
            <NText class="account-name"
              ><a
                class="account-link"
                target="_blank"
                :href="'https://x.com/' + item.x_account"
                >@{{ item.x_account }}</a
              ></NText
            >
          </div>
          <NDivider />
          <NSpace justify="space-around" :wrap="true">
            <NSpace vertical align="center" size="small" class="stat-item">
              <NText strong>{{ brandCount }}</NText>
              <NText depth="3">利用ブランド数</NText>
            </NSpace>
            <NSpace vertical align="center" size="small" class="stat-item">
              <NText strong>{{ productCount }}</NText>
              <NText depth="3">利用商品数</NText>
            </NSpace>
          </NSpace>
        </n-card>
      </NTabPane>
      <NTabPane name="similar-talent" tab="傾向が似ているアイドル">
        <NCard
          title="傾向が似ているアイドル"
          class="chart-card"
          :bordered="false"
        >
          <n-list hoverable clickable>
            <n-list-item
              v-for="item in item.similar_talents"
              :key="item.id"
              @click="handleTalentClick(item)"
            >
              <n-thing>
                <template #avatar>
                  <n-avatar :src="item.image_url" round :size="48" />
                </template>
                <template #header>
                  <n-text strong>{{ item.name }}</n-text>
                </template>
                <template #description>
                  <n-text depth="3">@{{ item.x_account }}</n-text>
                </template>
              </n-thing>
            </n-list-item>
          </n-list>
        </NCard>
      </NTabPane>
      <NTabPane name="brand" tab="利用ブランド">
        <NCard title="利用ブランド" class="chart-card" :bordered="false">
          <div class="chart-container">
            <Pie :data="brandData" :options="chartOptions" />
          </div>
        </NCard>
      </NTabPane>
      <NTabPane name="category" tab="利用カテゴリ">
        <NCard title="利用商品カテゴリー" class="chart-card" :bordered="false">
          <div class="chart-container">
            <Pie :data="categoryData" :options="chartOptions" />
          </div>
        </NCard>
      </NTabPane>
    </NTabs>
  </NCard>
</template>

<style scoped>
.profile-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.n-avatar {
  margin-bottom: 8px;
}

.n-h2 {
  margin-top: 8px;
  margin-bottom: 0px;
  color: #333;
  text-align: center;
  font-size: x-large;
}

.account-name {
  color: #666;
  margin-top: 0;
}

.n-text {
  text-align: center;
  margin-bottom: 16px;
  white-space: pre-line;
}

.stat-item {
  margin: 0;
}

.chart-card {
  height: 100%;
}

.chart-container {
  height: 300px;
  margin-bottom: 24px;
}

h4 {
  text-align: center;
  margin-bottom: 16px;
}

.account-link {
  color: gray;
  text-decoration: none;
}

@media (max-width: 992px) {
  .chart-container {
    height: 250px;
    margin-bottom: 20px;
  }
}
</style>
